import React, { memo, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { createActions } from '../../../store';
import { HexToRgb } from '../../../../../../utilities/colorUtilities';
import ColorPickerListItem from '../../../../../list-items/ColorPickerListItem';

const TOOLTIPS = {
    ADD: 'Click the "+" button to add a secondary color. The main color will change into the secondary color during loud noises.',
    REMOVE: 'Click the "🡪" button to remove the secondary color. The main color will change into the secondary color during loud noises.',
    BASIC: 'The main color will change into the secondary color during loud noises.'
};

const WaveLayerFillColorListItem = memo(({
    waveLayerFillColor,
    waveLayerFillAlpha,
    waveLayerSecondaryFillColor,
    waveLayerSecondaryFillAlpha,
    index,
    updateWaveLayer,
    advanced
}) => {
    const onColorChange = useCallback((color) => {
        updateWaveLayer(index, {
            fillColor: color.hex === 'transparent' ? '#000000' : color.hex.replace('#', '0x'),
            fillAlpha: color.rgb.a
        });
    }, [updateWaveLayer, index]);

    const onSecondaryColorChange = useCallback((color) => {
        if (color) {
            updateWaveLayer(index, {
                secondaryFillColor: color.hex === 'transparent' ? '#000000' : color.hex.replace('#', '0x'),
                secondaryFillAlpha: color.rgb.a
            });
        } else {
            updateWaveLayer(index, {
                secondaryFillColor: null
            });
        }
    }, [updateWaveLayer, index]);

    const colors = useMemo(() => {
        const fillColor = HexToRgb(waveLayerFillColor);
        fillColor.a = waveLayerFillAlpha;

        let secondaryFillColor = null;
        if (waveLayerSecondaryFillColor) {
            secondaryFillColor = HexToRgb(waveLayerSecondaryFillColor);
            secondaryFillColor.a = waveLayerSecondaryFillAlpha;
        }

        return { fillColor, secondaryFillColor };
    }, [waveLayerFillColor, waveLayerFillAlpha, waveLayerSecondaryFillColor, waveLayerSecondaryFillAlpha]);

    const tooltip = useMemo(() => {
        if (!advanced && !colors.secondaryFillColor) return null;
        if (advanced) {
            return colors.secondaryFillColor ? TOOLTIPS.REMOVE : TOOLTIPS.ADD;
        }
        return TOOLTIPS.BASIC;
    }, [advanced, colors.secondaryFillColor]);

    return (
        <ColorPickerListItem
            text='Layer Color'
            color={colors.fillColor}
            secondaryColor={colors.secondaryFillColor}
            onChange={onColorChange}
            onSecondaryChange={onSecondaryColorChange}
            allowSecondary={advanced || colors.secondaryFillColor}
            toolTip={tooltip}
            disableSecondaryButton={!advanced}
        />
    );
});

const mapStateToProps = (state, { index }) => {
    const {
        fillColor: waveLayerFillColor,
        fillAlpha: waveLayerFillAlpha,
        secondaryFillColor: waveLayerSecondaryFillColor,
        secondaryFillAlpha: waveLayerSecondaryFillAlpha
    } = state.createPreview.present.videoSettings.visualizer.waveCircles[index];

    return {
        waveLayerFillColor,
        waveLayerFillAlpha,
        waveLayerSecondaryFillColor,
        waveLayerSecondaryFillAlpha
    };
};

const mapDispatchToProps = {
    updateWaveLayer: (index, properties) => createActions.updateWaveLayer(index, properties)
};

export default connect(mapStateToProps, mapDispatchToProps)(WaveLayerFillColorListItem);